/**
 * Buyer Handoff Section (Phase 5B)
 *
 * Rendered on the realtor's listing-detail page once the listing is
 * marked sold. Lets the realtor:
 *   - dispatch a fresh handoff (capture buyer phone -> POST handoff)
 *   - see the per-listing handoff history with status pills
 *   - copy the public claim URL (so they can re-paste in WhatsApp)
 *   - reissue the link (one allowed) when the buyer has lost it
 *
 * The component fetches handoffs on mount via listMyHandoffs() and
 * filters client-side to the current listingId — keeps the API surface
 * minimal (no per-listing filter endpoint yet) and the realtor's
 * outgoing handoff count is small enough that the filter cost is fine.
 */

import {
	CheckCircle2,
	Clock,
	Copy,
	HandCoins,
	Loader2,
	RefreshCw,
	Send,
	XCircle,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
	type BuyerHandoff,
	createHandoff,
	listMyHandoffs,
	type RealtorHandoffRow,
	reissueHandoff,
} from "@/features/realtor/services/buyer-handoffs-api.service";
import { StudioButton } from "@/features/studio/theme";

interface BuyerHandoffSectionProps {
	listingId: string;
}

type LoadState =
	| { status: "loading" }
	| { status: "error"; message: string }
	| { status: "loaded"; rows: RealtorHandoffRow[] };

export function BuyerHandoffSection({ listingId }: BuyerHandoffSectionProps) {
	const { t } = useTranslation();
	const [load, setLoad] = useState<LoadState>({ status: "loading" });
	const [showDispatchDialog, setShowDispatchDialog] = useState(false);

	/**
	 * Reload the handoff list from the server. The `preserveRows`
	 * argument keeps an optimistic prepend visible even if the refresh
	 * fails (network blip, 500) — issue #136. Without it, a failed
	 * post-create refresh wiped the just-dispatched handoff and the
	 * realtor saw "couldn't load handoffs" instead of their fresh row.
	 */
	const refresh = useCallback(
		async (preserveRows?: RealtorHandoffRow[]) => {
			// Show the loading skeleton only on the initial load, not on
			// post-mutation refreshes — the existing rows stay visible
			// (and so does any optimistic row we want preserved).
			if (!preserveRows) setLoad({ status: "loading" });
			const result = await listMyHandoffs();
			if (!result.success || !result.data) {
				if (preserveRows && preserveRows.length > 0) {
					// Refresh failed but we have an optimistic state worth
					// keeping; surface a toast and stay loaded so the user
					// sees their just-created row.
					toast.error(result.error ?? t("realtor.handoff.loadFailedFallback"));
					setLoad({ status: "loaded", rows: preserveRows });
					return;
				}
				setLoad({
					status: "error",
					message: result.error ?? t("realtor.handoff.loadFailedFallback"),
				});
				return;
			}
			const filtered = result.data.filter(
				(row) => row.handoff.listingId === listingId,
			);
			setLoad({ status: "loaded", rows: filtered });
		},
		[listingId, t],
	);

	useEffect(() => {
		refresh();
	}, [refresh]);

	const handleCreated = (handoff: BuyerHandoff) => {
		setShowDispatchDialog(false);
		// Compute the optimistic-prepended rows OUTSIDE the setLoad
		// updater. React 18 strict mode + concurrent rendering can run
		// the updater more than once; relying on a side-effect inside
		// it would mean `preserved` is set 0, 1, or 2 times depending
		// on the render mode. Reading load.status is fine — it's a
		// hook value, stable across the synchronous handler body.
		const baseRows = load.status === "loaded" ? load.rows : [];
		const optimisticRow: RealtorHandoffRow = {
			handoff,
			listing: {
				id: listingId,
				headline: "",
				addressPublic: "",
				city: "",
				locality: "",
			},
		};
		const preserved: RealtorHandoffRow[] = [optimisticRow, ...baseRows];
		setLoad({ status: "loaded", rows: preserved });
		// Sync with the server so we get the joined listing snippet.
		// If the refresh fails, preserved keeps the optimistic row alive.
		refresh(preserved);
	};

	return (
		<Card className="bg-dashboard-bg border-dashboard-border rounded-xl shadow-[var(--dashboard-shadow-2)]">
			<CardContent className="p-5 space-y-4">
				<header className="flex items-center justify-between">
					<div>
						<p
							style={{
								fontFamily: "var(--font-mono)",
								fontSize: 11,
								fontWeight: 600,
								letterSpacing: "0.18em",
								textTransform: "uppercase",
								color: "var(--color-dashboard-muted)",
							}}
						>
							<HandCoins className="h-3 w-3 inline mr-1" />
							{t("realtor.handoff.sectionTitle")}
						</p>
						<h2
							style={{
								fontFamily: "var(--font-display)",
								fontSize: 20,
								fontWeight: 500,
								fontStyle: "italic",
								color: "var(--color-dashboard-text)",
								marginTop: 4,
								lineHeight: 1.1,
							}}
						>
							{t("realtor.handoff.sectionSubtitle")}
						</h2>
					</div>
					<StudioButton
						size="sm"
						className="gap-1.5"
						onClick={() => setShowDispatchDialog(true)}
					>
						<Send className="h-4 w-4" />
						{t("realtor.handoff.dispatchCta")}
					</StudioButton>
				</header>

				{load.status === "loading" ? (
					<div className="space-y-2" data-testid="handoffs-loading">
						<Skeleton className="h-16 w-full bg-dashboard-surface" />
					</div>
				) : load.status === "error" ? (
					<div
						className="rounded-md border border-dashboard-border bg-dashboard-surface p-3 text-xs"
						role="alert"
					>
						<p className="text-dashboard-muted">{load.message}</p>
						<StudioButton
							size="sm"
							variant="secondary"
							className="mt-2 gap-1.5 border-dashboard-border text-dashboard-text"
							onClick={() => refresh()}
						>
							<RefreshCw className="h-3 w-3" />
							{t("realtor.handoff.retry")}
						</StudioButton>
					</div>
				) : load.rows.length === 0 ? (
					<p className="text-xs text-dashboard-muted">
						{t("realtor.handoff.empty")}
					</p>
				) : (
					<ul className="space-y-2">
						{load.rows.map((row) => {
							// Capture the currently-loaded rows so a post-reissue
							// refresh failure preserves them (matches the
							// dispatch-success behavior). Without this, a flaky
							// refresh after reissue would wipe the entire list.
							const currentRows = load.rows;
							return (
								<HandoffRow
									key={row.handoff.id}
									row={row}
									onReissued={() => refresh(currentRows)}
								/>
							);
						})}
					</ul>
				)}
			</CardContent>

			<DispatchHandoffDialog
				open={showDispatchDialog}
				onClose={() => setShowDispatchDialog(false)}
				listingId={listingId}
				onCreated={handleCreated}
			/>
		</Card>
	);
}

function HandoffRow({
	row,
	onReissued,
}: {
	row: RealtorHandoffRow;
	onReissued: () => void;
}) {
	const { t } = useTranslation();
	const [busy, setBusy] = useState(false);
	const unmountedRef = useRef(false);
	const { handoff } = row;

	useEffect(() => {
		unmountedRef.current = false;
		return () => {
			unmountedRef.current = true;
		};
	}, []);

	const claimUrl = `${window.location.origin}/claim/${handoff.handoffToken}`;

	const copyLink = async () => {
		try {
			await navigator.clipboard.writeText(claimUrl);
			toast.success(t("realtor.handoff.copiedToast"));
		} catch (err) {
			// Permissions-Policy denial / insecure context / Safari
			// user-gesture loss all land here. Log so Sentry can see
			// it; the realtor can long-press the URL or read it from
			// the row's text manually.
			console.warn("[BuyerHandoff] copy-link failed", err);
			toast.error(t("realtor.handoff.copyFailed"));
		}
	};

	const handleReissue = async () => {
		setBusy(true);
		const result = await reissueHandoff(handoff.id);
		if (unmountedRef.current) return;
		setBusy(false);
		if (!result.success) {
			toast.error(result.error ?? t("realtor.handoff.reissueFailed"));
			return;
		}
		toast.success(t("realtor.handoff.reissuedToast"));
		onReissued();
	};

	const phoneDisplay = `+${handoff.buyerPhoneCountryCode} ${handoff.buyerPhone}`;
	const canReissue =
		handoff.reissueCount < 1 &&
		handoff.status !== "onboarded" &&
		handoff.status !== "expired";

	return (
		<li className="rounded-lg border border-dashboard-border bg-dashboard-bg p-3 space-y-2 hover:bg-dashboard-surface transition-colors">
			<div className="flex items-start justify-between gap-3">
				<div className="min-w-0">
					{/* data-sensitive: masked in session replay — a buyer's phone
					    number, and the buyer is not the one who could switch
					    recording off. See lib/posthog.ts. */}
					<p
						data-sensitive
						className="text-sm font-medium text-dashboard-text truncate"
					>
						{phoneDisplay}
					</p>
					<p className="text-[11px] text-dashboard-muted">
						{t("realtor.handoff.sentOn", {
							date: new Date(handoff.sentAt).toLocaleDateString(),
						})}
					</p>
				</div>
				<StatusPill status={handoff.status} />
			</div>
			<div className="flex flex-wrap gap-1.5">
				<StudioButton
					size="sm"
					variant="secondary"
					className="h-7 gap-1 text-[11px] border-dashboard-border text-dashboard-text"
					onClick={copyLink}
					disabled={
						handoff.status === "onboarded" || handoff.status === "expired"
					}
				>
					<Copy className="h-3 w-3" />
					{t("realtor.handoff.copyLink")}
				</StudioButton>
				{canReissue && (
					<StudioButton
						size="sm"
						variant="secondary"
						className="h-7 gap-1 text-[11px] border-dashboard-border text-dashboard-text"
						disabled={busy}
						onClick={handleReissue}
					>
						{busy ? (
							<Loader2 className="h-3 w-3 animate-spin" />
						) : (
							<RefreshCw className="h-3 w-3" />
						)}
						{t("realtor.handoff.reissue")}
					</StudioButton>
				)}
			</div>
		</li>
	);
}

function StatusPill({ status }: { status: BuyerHandoff["status"] }) {
	const { t } = useTranslation();
	const tone = STATUS_TONES[status];
	const Icon = STATUS_ICONS[status];
	return (
		<span
			className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${tone}`}
		>
			<Icon className="h-3 w-3" />
			{t(`realtor.handoff.status.${status}`)}
		</span>
	);
}

const STATUS_TONES: Record<BuyerHandoff["status"], string> = {
	sent: "bg-dashboard-pill-info-bg text-dashboard-pill-info",
	clicked: "bg-dashboard-pill-awaiting-bg text-dashboard-pill-awaiting",
	onboarded: "bg-dashboard-pill-done-bg text-dashboard-pill-done",
	expired: "bg-dashboard-pill-neutral-bg text-dashboard-pill-neutral",
};

const STATUS_ICONS: Record<
	BuyerHandoff["status"],
	React.ComponentType<{ className?: string }>
> = {
	sent: Send,
	clicked: Clock,
	onboarded: CheckCircle2,
	expired: XCircle,
};

// ----------------------------------------------------------------------
// Dispatch dialog
// ----------------------------------------------------------------------

function DispatchHandoffDialog({
	open,
	onClose,
	listingId,
	onCreated,
}: {
	open: boolean;
	onClose: () => void;
	listingId: string;
	onCreated: (handoff: BuyerHandoff) => void;
}) {
	const { t } = useTranslation();
	const [phone, setPhone] = useState("");
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const reset = useCallback(() => {
		setPhone("");
		setBusy(false);
		setError(null);
	}, []);

	useEffect(() => {
		if (!open) reset();
	}, [open, reset]);

	const submit = async (event: React.FormEvent) => {
		event.preventDefault();
		// Strip everything that's not a digit, then enforce the 10-digit
		// Indian mobile format the UX copy promises (and the backend's
		// validatePhoneNumber accepts). Catches obvious mistakes
		// (letters, short pastes, leading +91 in addition to country
		// code field) before round-tripping to the backend.
		const digitsOnly = phone.replace(/\D/g, "");
		const trimmed =
			digitsOnly.startsWith("91") && digitsOnly.length === 12
				? digitsOnly.slice(2)
				: digitsOnly;
		if (!/^[6-9]\d{9}$/.test(trimmed)) {
			setError(t("realtor.handoff.dispatch.phoneInvalid"));
			return;
		}
		setBusy(true);
		setError(null);
		const result = await createHandoff({
			listingId,
			buyerPhone: trimmed,
		});
		setBusy(false);
		if (!result.success || !result.data) {
			setError(result.error ?? t("realtor.handoff.dispatch.failed"));
			return;
		}
		toast.success(t("realtor.handoff.dispatch.successToast"));
		onCreated(result.data);
	};

	return (
		<Dialog open={open} onOpenChange={(next) => (next ? null : onClose())}>
			<DialogContent className="bg-dashboard-bg border-dashboard-border rounded-xl">
				<form onSubmit={submit}>
					<DialogHeader>
						<DialogTitle
							style={{
								fontFamily: "var(--font-display)",
								fontSize: 20,
								fontWeight: 500,
								fontStyle: "italic",
								color: "var(--color-dashboard-text)",
							}}
						>
							{t("realtor.handoff.dispatch.title")}
						</DialogTitle>
						<DialogDescription className="text-sm text-dashboard-muted">
							{t("realtor.handoff.dispatch.subtitle")}
						</DialogDescription>
					</DialogHeader>
					<div className="space-y-3 py-3">
						<div className="space-y-1.5">
							<Label
								htmlFor="buyer-phone"
								className="text-sm text-dashboard-text"
							>
								{t("realtor.handoff.dispatch.phoneLabel")}
							</Label>
							<Input
								id="buyer-phone"
								type="tel"
								inputMode="tel"
								placeholder="9876543210"
								value={phone}
								onChange={(e) => setPhone(e.target.value)}
								className="border-dashboard-border bg-dashboard-surface text-dashboard-text placeholder:text-dashboard-muted"
								aria-invalid={error ? true : undefined}
								aria-describedby={
									error ? "buyer-phone-error" : "buyer-phone-hint"
								}
							/>
							<p
								id="buyer-phone-hint"
								className="text-[11px] text-dashboard-muted"
							>
								{t("realtor.handoff.dispatch.phoneHint")}
							</p>
						</div>
						{error && (
							<p
								id="buyer-phone-error"
								className="text-xs text-dashboard-pill-risk"
								role="alert"
							>
								{error}
							</p>
						)}
					</div>
					<DialogFooter>
						<StudioButton
							type="button"
							variant="secondary"
							className="border-dashboard-border text-dashboard-text"
							onClick={onClose}
							disabled={busy}
						>
							{t("realtor.handoff.dispatch.cancel")}
						</StudioButton>
						<StudioButton type="submit" disabled={busy} className="gap-1.5">
							{busy && <Loader2 className="h-4 w-4 animate-spin" />}
							{t("realtor.handoff.dispatch.submit")}
						</StudioButton>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
